import queryString from 'query-string';

import { STARTER_PLAN_OVERRIDE } from '../constants/localStorage';
import { PLANS, type Plan } from './plans';

/**
 * The URL param this axis owns, end to end.
 *
 * Exported so `src/utils/previewOverrideUrl.ts` can consume and strip it without
 * re-declaring the name. The promote/strip side effect lives there rather than here
 * because it needs the app's `history` singleton, which cannot be imported into this
 * module: `createBrowserHistory()` runs at module scope and throws without a DOM, and
 * `starterPreviewOverride.test.ts` exercises the pure resolver below in the node project.
 */
export const STARTER_PLAN_PARAM = 'preview_plan';

/**
 * The slug Nexoya Starter shipped under before the rename — already persisted in support
 * engineers' localStorage and embedded in shared `?preview_plan=` links, so it cannot be deleted.
 */
const LEGACY_STARTER_SLUG = 'nexoya4agency';

/**
 * The only plan the override may resolve to — deliberately NOT `Object.values(PLANS)`.
 *
 * The preview only ever DOWNGRADES. `?preview_plan=premium` on a Nexoya Starter team would
 * unlock every premium tab straight from a URL, and PREMIUM is never legitimately needed: the
 * one setter call site (`Account.tsx`) passes STARTER or null. Mirrors
 * `PREVIEWABLE_ROLES` in `src/Auth/rbac/rolePreviewOverride.ts`, which excludes `support`
 * for the same reason.
 */
const PREVIEWABLE_PLANS: ReadonlyArray<string> = [PLANS.STARTER];

function readPlan(raw: unknown): Plan | null {
  if (typeof raw !== 'string') return null;
  if (raw === LEGACY_STARTER_SLUG) return PLANS.STARTER;
  return PREVIEWABLE_PLANS.includes(raw) ? (raw as Plan) : null;
}

/**
 * Resolves the support/dev plan override from its two sources, URL param first.
 *
 * Returns `null` for "no override" — deliberately NOT `PLANS.PREMIUM`. Resolving an
 * unusable value to PREMIUM would silently mask a real Nexoya Starter team while looking
 * like the override worked.
 *
 * Pure so it can be exercised in the node test project; the `window` reads live in
 * `getStarterPlanOverride` below.
 */
export function resolveStarterPlanOverride({
  search,
  stored,
}: {
  search?: string | null;
  stored?: string | null;
}): Plan | null {
  const fromUrl = search ? readPlan(queryString.parse(search)[STARTER_PLAN_PARAM]) : null;
  return fromUrl ?? readPlan(stored);
}

/**
 * Support-user local override that forces the app to render as a given plan, independent
 * of the BE `Team.plan` field. Mirrors `isPresenterMode()`
 * (`src/utils/isPresenterMode.ts`) for the URL half and the header auto-collapse preference
 * for the localStorage half.
 */
export function getStarterPlanOverride(): Plan | null {
  return resolveStarterPlanOverride({
    search: window.location.search,
    // The ACCESS throws in a locked or private-mode profile — see `rolePreviewOverride.ts`.
    stored: readStored(STARTER_PLAN_OVERRIDE),
  });
}

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function setStarterPlanOverride(plan: Plan | null): void {
  try {
    if (plan === null) {
      window.localStorage.removeItem(STARTER_PLAN_OVERRIDE);
    } else {
      window.localStorage.setItem(STARTER_PLAN_OVERRIDE, plan);
    }
  } catch {
    // A blocked store costs the preview its persistence, nothing else.
  }
  // Same-tab listeners: the native `storage` event only fires in *other* tabs.
  window.dispatchEvent(new CustomEvent(STARTER_PLAN_OVERRIDE_EVENT));
}

export const STARTER_PLAN_OVERRIDE_EVENT = 'nexoya:plan-preview-override';
